import { GlobalLazyLoader, GlobalLoaderLayout, Loader } from "@kiddocare-platform/portal-common/ui";
import React from "react";
import { createBrowserRouter, createRoutesFromElements, Navigate, Outlet, Route, RouterProvider, useLocation } from "react-router-dom";
import { useParentContext } from "../contexts/parent.context";
import ErrorPage from "./error.page";
import NotFoundPage from "./not-found.page";

const ProtectedRoute: React.FC = () => {
  const location = useLocation();
  const { isInitialized, isParentLoaded, isPatientsLoaded, user, isSettingsLoaded, settings, isSubscriptionCanceled } = useParentContext();
  if (!isInitialized) {
    return <Loader mode="overlay" isShowing />;
  }
  if (!user) {
    return <Navigate to="/auth/login" replace />;
  }
  if (!isSettingsLoaded) {
    return <Loader mode="overlay" isShowing />;
  }
  const hasIncompleteMandatory = settings?.some(s => s.blocking && !s.completed);

  if (hasIncompleteMandatory && !location.pathname.startsWith("/settings") && !isSubscriptionCanceled) {
    return <Navigate to="/settings" replace />;
  }
  if (!isParentLoaded || !isPatientsLoaded) {
    return <Loader mode="overlay" isShowing />;
  }
  return <Outlet />;
};

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<GlobalLoaderLayout />} errorElement={<ErrorPage />}>

      {/* AUTH */}
      <Route path="/auth" lazy={GlobalLazyLoader(() => import('./auth/auth.page'))}>
        <Route path="login" lazy={GlobalLazyLoader(() => import('./auth/login.page'))} />
        <Route path="logout" lazy={GlobalLazyLoader(() => import('./auth/logout.page'))} />
        <Route path="callback" lazy={GlobalLazyLoader(() => import('./auth/callback.page'))} />
        <Route path="postlogout" lazy={GlobalLazyLoader(() => import('./auth/postlogout.page'))} />
      </Route>

      {/* PROTECTED ROUTES */}
      <Route element={<ProtectedRoute />}>
        {/* MAIN PAGE è il layout principale loggato */}
        <Route path="/" lazy={GlobalLazyLoader(() => import('./main.page'))}>

          <Route index element={<Navigate to="common" replace />} />

          {/* COMMON */}
          <Route path="common">
            <Route index element={<Navigate to="home" replace />} />
            <Route path="home" lazy={GlobalLazyLoader(() => import('./common/home.page'))} />
            <Route path="notifications" lazy={GlobalLazyLoader(() => import('./common/notifications.page'))} />
            <Route path="patient/:idAccountPatient/add-pediatrician/success" lazy={GlobalLazyLoader(() => import('./common/add-pediatrician-success.page'))} />
            <Route path="patient/:idAccountPatient/add-pediatrician" lazy={GlobalLazyLoader(() => import('./common/add-pediatrician.page'))} />
            <Route path="patient/:idAccountPatient/add-specialist/:idSpecialist" lazy={GlobalLazyLoader(() => import('./common/add-specialist-detail.page'))} />
            <Route path="payment" lazy={GlobalLazyLoader(() => import('./common/payment.page'))} />
          </Route>

          {/* CHATBOT */}
          <Route path="chatbot" lazy={GlobalLazyLoader(() => import('./common/chatbot.page'))} />

          <Route path="parent" lazy={GlobalLazyLoader(() => import('./parent/parent.page'))} >
            <Route index element={<Navigate to="info" replace />} />
            <Route path="info" lazy={GlobalLazyLoader(() => import('./parent/info.page'))} />
            <Route path="family" lazy={GlobalLazyLoader(() => import('./parent/family-management.page'))} />
            <Route path="personal-data" lazy={GlobalLazyLoader(() => import('./parent/family-parent-personal.page'))} />
            <Route path="addresses" lazy={GlobalLazyLoader(() => import('./parent/family-parent-adresses.page'))} />
            <Route path="identification-document" lazy={GlobalLazyLoader(() => import('./parent/family-parent-identification.page'))} />
            <Route path="legal" lazy={GlobalLazyLoader(() => import('./parent/legal.page'))} />
            <Route path="billing" lazy={GlobalLazyLoader(() => import('./parent/billing.page'))} />
            <Route path="delete-account" lazy={GlobalLazyLoader(() => import('./parent/delete-account.page'))} />
            <Route path="menage_account" lazy={GlobalLazyLoader(() => import('./parent/parent-account.page'))} />
          </Route>

          {/* SETTINGS */}
          <Route path="/settings" lazy={GlobalLazyLoader(() => import('./settings/settings.page'))}>
            <Route index element={<Navigate to="intro" replace />} />
            <Route path="intro" lazy={GlobalLazyLoader(() => import('./settings/intro.page'))} />
            <Route path="policy-accepted" lazy={GlobalLazyLoader(() => import('./settings/policy-accepted.page'))} />
            <Route path="registration-choice" lazy={GlobalLazyLoader(() => import('./settings/registration-choice.page'))} />
            <Route path="addresses" lazy={GlobalLazyLoader(() => import('./settings/addresses.page'))} />
            <Route path="identification-document" lazy={GlobalLazyLoader(() => import('./settings/load-document-parent.page'))} />
            <Route path="completed" lazy={GlobalLazyLoader(() => import('./settings/completed.page'))} />
          </Route>

          {/* PATIENT */}
          <Route path="patient/:idAccountPatient/:idAccountPediatrician" lazy={GlobalLazyLoader(() => import('./patient/pediatrician-patient.page'))} >
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" lazy={GlobalLazyLoader(() => import('./patient/dashboard.page'))} />
            <Route path="prescriptions" lazy={GlobalLazyLoader(() => import('./patient/prescriptions.page'))} />
            <Route path="prescriptions/request" lazy={GlobalLazyLoader(() => import('./patient/prescription-request.page'))} />
            <Route path="prescriptions/success" lazy={GlobalLazyLoader(() => import('./patient/prescription-success.page'))} />
            <Route path="certificates" lazy={GlobalLazyLoader(() => import('./patient/certifications.page'))} />
            <Route path="certificates/request" lazy={GlobalLazyLoader(() => import('./patient/certification-request.page'))} />
            <Route path="certificates/request/:idCertificate" lazy={GlobalLazyLoader(() => import('./patient/certification-request-detail.page'))} />
            <Route path="certificates/success" lazy={GlobalLazyLoader(() => import('./patient/certification-success.page'))} />
            <Route path="visits" lazy={GlobalLazyLoader(() => import('./patient/visits.page'))} />
            <Route path="visits/request" lazy={GlobalLazyLoader(() => import('./patient/visit-request.page'))} />
            <Route path="visits/success" lazy={GlobalLazyLoader(() => import('./patient/visit-success.page'))} />
            <Route path="consultations" lazy={GlobalLazyLoader(() => import('./patient/consultations.page'))} />
            <Route path="consultations/request" lazy={GlobalLazyLoader(() => import('./patient/consultation-request.page'))} />
            <Route path="consultations/:ticket" lazy={GlobalLazyLoader(() => import('./patient/consultation.page'))} />
            <Route path="messages" lazy={GlobalLazyLoader(() => import('./patient/messages.page'))} />
          </Route>
          <Route path="patient/:idAccountPatient/grow-together/:code?/:idEvent?" lazy={GlobalLazyLoader(() => import('./patient/grow-together.page'))} />
          <Route path="patient/:idAccountPatient/grow-together/preferences" lazy={GlobalLazyLoader(() => import('./patient/grow-together-preferences.page'))} />
          <Route path="patient/:idAccountPatient/grow-together/event/:code/:idEvent?" lazy={GlobalLazyLoader(() => import('./patient/grow-together-events.page'))} />
          <Route path="patient/:idAccountPatient/grow-together/statistics" lazy={GlobalLazyLoader(() => import('./patient/grow-together-statistics.page'))} />
          <Route path="patient/:idAccountPatient/clinical-history" lazy={GlobalLazyLoader(() => import('./patient/clinical-history.page'))} />
          <Route path="patient/:idAccountPatient/personal-data" lazy={GlobalLazyLoader(() => import('./patient/patient-family-personal-data.page'))} />
          <Route path="patient/:idAccountPatient/identification-document" lazy={GlobalLazyLoader(() => import('./patient/patient-family-identification-document.page'))} />
          <Route path="patient/:idAccountPatient" lazy={GlobalLazyLoader(() => import('./patient/patient.page'))} />
          <Route path="patient/:idAccountPatient/menage_account" lazy={GlobalLazyLoader(() => import('./patient/patient-account.page'))} />
        </Route>
        <Route path="*" element={(<NotFoundPage />)} />
      </Route>
    </Route>
  )
);

const PagesRoutes: React.FC = () => {
  return (<RouterProvider router={router} />);
}

export { PagesRoutes };
